import { Component, OnDestroy, OnInit } from "@angular/core";
import { SharedComponentsModule } from "../../../shared/module/shared-components.module";
import { TableBudgetModelComponent } from "../budget-table/table-budget-model.component";
import { BudgetFormService } from "../../services/general/bm-form.service";
import { BudgetModelFormGroup } from "../../pojo/bm-form";
import { HeaderBudgetModelComponent } from "../budget-header/header-budget-model.component";
import { firstValueFrom, Observable, Subject, take, takeUntil } from "rxjs";
import { BudgetModel, BudgetModelSelectorMiniDTO } from "../../pojo/bm-classes";
import { BmDefaultFormCreatorService } from "../../services/general/bm-default-form-creator.service";
import { BudgetModelBackendService } from "../../services/backend/budget-model-backend.service";
import { OrganizationsService } from "../../../organization/components/service/organizations.service";
import { Organization } from "../../../organization/dto/Organization";
import { NOTIFICATIONS, NotificationsService } from "../../../../core/service/notifications.service";
import { BmButtons } from "../../pojo/bm-enums";
import { BmGeneralService } from "../../services/general/bm-general-service";
import { UnsavedChangesModalService } from "../../../shared/component/can-deactivate-guard/unsaved-changes-modal.service";
import { CanDeactivateComponent } from "../../../shared/component/can-deactivate-guard/can-deactivate-guard-interface";
import { filter } from "rxjs/operators";
import { BmLoadingStateComponent } from "../loading-state/bm-loading-state.component";

@Component({
  selector: 'app-fund-budget-model',
  templateUrl: './fund-budget-model.component.html',
  styleUrls: ['./fund-budget-model.component.scss'],
  standalone: true,
  imports: [
    SharedComponentsModule,
    TableBudgetModelComponent,
    HeaderBudgetModelComponent,
    BmLoadingStateComponent,
  ],
})

export class FundBudgetModelComponent implements OnInit, OnDestroy, CanDeactivateComponent {
  private _destroyStream: Subject<void> = new Subject<void>();

  model: BudgetModel;
  budgetForm: BudgetModelFormGroup;
  budgetDTO: BudgetModel;
  selectedOrganization: Organization;
  selectorData: BudgetModelSelectorMiniDTO[];
  loadingState = true;

  constructor(
    private budgetModelBackendService: BudgetModelBackendService,
    private organizationsService: OrganizationsService,
    private notificationsService: NotificationsService,
    private unsavedChangesModalService: UnsavedChangesModalService,
  ) {}


  ngOnInit(): void {
    this.getOrganization();
  }

  ngOnDestroy(): void {
    this._destroyStream.next();
    this._destroyStream.complete();
  }

  getOrganization(): void {
    this.organizationsService.selected$
      .pipe(
        takeUntil(this._destroyStream),
        filter((org) => Boolean(org)))
      .subscribe((org) => {
        this.loadingState = true;
        this.selectedOrganization = org;
        this.getInitialModelData();
      });
  }

  async changeModel(modelUuid: string): Promise<void> {
    this.loadingState = true;
    this.budgetDTO = await firstValueFrom(this.budgetModelBackendService.get(modelUuid).pipe(takeUntil(this._destroyStream)));
    this.initModel();
  }

  buttonClicked(action: BmButtons): void {
    switch (action) {
      case BmButtons.CANCEL:
        this.loadingState = true;
        this.initModel();
        break;
      case BmButtons.ADD_NEW_MODEL:
        this.loadingState = true;
        this.addNewModel();
        break;
      case BmButtons.CREATE:
        this.loadingState = true;
        this.createModel();
        break;
      case BmButtons.UPDATE:
        this.loadingState = true;
        this.updateModel();
        break;
      case BmButtons.DELETE:
        this.loadingState = true;
        this.deleteModel();
        break;
      case BmButtons.DUPLICATE:
        this.loadingState = true;
        this.duplicateModel();
        break;
    }
  }

  /**
   * Prevent leaving the page when user has unsaved changes
   */
  canDeactivate(): Observable<boolean> | boolean {
    if (this.unsavedChangesModalService.getIsInEditMode()) {
      return this.unsavedChangesModalService.showConfirmDialog();
    }
    return true;
  }

  private initModel(isNewModel = false): void {
    this.model = isNewModel ? BmDefaultFormCreatorService.createNewBudgetModelDefault() : this.budgetDTO;
    this.updateSelectorData(this.model);
    this.budgetForm = BudgetFormService.createBudgetForm(this.model, this.selectedOrganization, isNewModel);
    this.budgetForm.valueChanges
      .pipe(take(1)) // only on first change
      .subscribe(() => {
        this.unsavedChangesModalService.setIsInEditMode(true);
      });
    this.loadingState = false;
  }

  private async getInitialModelData(): Promise<void> {
    const modelWrapper = await firstValueFrom(this.budgetModelBackendService.getInitialModelData(this.selectedOrganization.uuid).pipe(takeUntil(this._destroyStream)));
    this.selectorData = modelWrapper.selectorData ?? [];
    this.budgetDTO = modelWrapper.selectedModel ?? null;
    this.initModel(!modelWrapper.selectedModel);
  }

  private updateSelectorData(model: BudgetModel): void {
    if (!model.uuid) {
      return;
    }
    const selectedModel = this.selectorData.find((selectorModel) => selectorModel.uuid === model.uuid);
    if (selectedModel) {
      selectedModel.name = model.name;
      selectedModel.selectorText = BmGeneralService.createSelectorText(model);
    } else {
      const newModel = new BudgetModelSelectorMiniDTO();
      newModel.uuid = model.uuid;
      newModel.orgUuid = model.orgUuid;
      newModel.name = model.name;
      newModel.lastModifiedDate = model.lastModifiedDate;
      newModel.createdDate = model.createdDate;
      newModel.createdBy = model.createdBy;
      newModel.selectorText = BmGeneralService.createSelectorText(model);
      this.selectorData.push(newModel);
    }
  }

  private async createModel(): Promise<void> {
    this.budgetForm.controls.isEditState.patchValue(false);
    const dto = BudgetFormService.mapFormToDto(this.budgetForm);
    this.budgetDTO = await firstValueFrom(this.budgetModelBackendService.create(dto).pipe(takeUntil(this._destroyStream)));
    this.initModel();
    this.notificationsService.success(NOTIFICATIONS.BUDGET_MODEL_WAS_CREATED);
  }

  private async deleteModel(): Promise<void> {
    await firstValueFrom(this.budgetModelBackendService.delete(this.budgetForm.value.uuid).pipe(takeUntil(this._destroyStream)));
    this.notificationsService.success(NOTIFICATIONS.BUDGET_MODEL_DELETED_SUCCESSFULLY);
    this.getInitialModelData();
  }

  private async duplicateModel(): Promise<void> {
    this.budgetDTO = await firstValueFrom(this.budgetModelBackendService.duplicate(this.budgetForm.getRawValue().uuid).pipe(takeUntil(this._destroyStream)));
    this.initModel();
    this.notificationsService.success(NOTIFICATIONS.BUDGET_MODEL_DUPLICATED_SUCCESSFULLY);
  }

  private addNewModel(): void {
    this.initModel(true);
    this.budgetForm.controls.isEditState.patchValue(true);
  }

  private async updateModel(): Promise<void> {
    this.budgetForm.controls.isEditState.patchValue(false);
    const dto = BudgetFormService.mapFormToDto(this.budgetForm);
    this.budgetDTO = await firstValueFrom(this.budgetModelBackendService.update(dto).pipe(takeUntil(this._destroyStream)));
    this.initModel();
    this.notificationsService.success(NOTIFICATIONS.BUDGET_MODEL_UPDATED_SUCCESSFULLY);
  }
}
